import React, { useState, useMemo, useEffect } from "react";
// import { API } from "aws-amplify";
import { Button, withStyles } from "@material-ui/core";
import { withTranslation } from "react-i18next";
import AccountMenu from "./account-menu";
import LoginForm from "../../containers/LoginForm";
import Logo from "../../images/fansfirst-logo.svg";
import LogoFr from "../../images/fansfirst-logo-fr.svg";

import {
  getSavedLang,
  saveLang,
  guessLangFromBrowser,
  getLangFromUrl,
  setLangInUrl,
  withLang,
} from "../../utils/lang";
import { PHONE_QC, PHONE_DEFAULT } from "../../utils/phones";
import analytics from "../../analytics";

const styles = (theme) => ({
  navContainer: {
    position: "relative",
    boxShadow: "0 4px 6px 0 rgb(0 0 0 / 3%)",
    backgroundColor: "#fafafa",
    width: "100%",
    zIndex: theme.zIndex.drawer + 1,
  },
  navContainerMobile: {
    position: "relative",
    boxShadow: "0 4px 6px 0 rgb(0 0 0 / 3%)",
    backgroundColor: "#fafafa",
    width: "100%",
    zIndex: "999",
  },
  nav: {
    maxWidth: 1366,
    height: "80px",
    margin: "0 auto",
    display: "flex",
  },
  logoContainer: {
    display: "block",
    margin: "auto",
  },
  callUsContainer: {
    position: "relative",
    padding: theme.spacing.unit * 2,
    flex: 1,
    textDecoration: "none",
  },
  logo: {
    verticalAlign: "middle",
  },
  linksContainer: {
    display: "flex",
    padding: theme.spacing.unit * 2,
    flex: 1,
  },
  linksContainerAuth: {
    display: "flex",
    padding: `9px ${theme.spacing.unit * 2}px`,
    flex: 1,
    verticalAlign: "middle",
  },
  navLinks: {
    flex: 1,
    minHeight: theme.spacing.unit * 5,
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
  },
  accountLinksContainer: {
    minHeight: theme.spacing.unit * 5,
    borderRight: "1px solid #ddd",
  },
  questionsText: {
    color: "#00A0FF",
    fontWeight: "bold",
    width: "90%",
    marginTop: "110px",
    fontSize: "13.5pt",
  },
  phoneNumberText: {
    color: "#5F666F",
    fontWeight: "bold",
    width: "90%",
    marginTop: "110px",
    fontSize: "13.5pt",
  },
  sellButton: {
    height: "46px",
    minWidth: theme.spacing.unit * 12,
    margin: `0 ${theme.spacing.unit * 2}px`,
    backgroundColor: "#00de94",
    color: "#ffffff",
    "&:hover": {
      color: "#ffffff",
      backgroundColor: "#00c281",
    },
    textTransform: "none",
    textAlign: "center",
  },
  sellButtonAuth: {
    height: "46px",
    minWidth: theme.spacing.unit * 12,
    margin: `8px ${theme.spacing.unit * 2}px`,
    backgroundColor: "#00de94",
    color: "#ffffff",
    "&:hover": {
      color: "#ffffff",
      backgroundColor: "#00c281",
    },
    textTransform: "none",
    textAlign: "center",
  },
  registerButton: {
    height: "46px",
    margin: `0 ${theme.spacing.unit}px`,
    color: "#000000",
    textTransform: "none",
    textAlign: "center",
  },
  loginButton: {
    height: "46px",
    margin: `0 ${theme.spacing.unit}`,
    backgroundColor: "#00a0ff",
    color: "white",
    "&:hover": {
      color: "#ffffff",
      backgroundColor: "#0098f3",
    },
    textTransform: "none",
    textAlign: "center",
  },
  [theme.breakpoints.down("md")]: {
    button: { margin: `0` },
    textTransform: "none",
  },
  langToggle: {
    display: "inline-flex",
    border: "1px solid #BDBDBD",
    borderRadius: 999,
    overflow: "hidden",
    height: 32,
    minWidth: "fit-content",
    maxWidth: "75px",
    background: "#fff",
    flexShrink: 0,
    fontSize: "11px",
  },
  langBtn: {
    padding: "0 6px",
    fontWeight: 700,
    lineHeight: "30px",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    color: "#000",
    flex: "0 1 auto",
    textAlign: "center",
    whiteSpace: "nowrap",
    fontSize: "11px",
    minWidth: "auto",
  },
  langBtnActive: { 
    background: "#2196F3", 
    color: "#fff", 
    fontWeight: 800 
  },
});

const renderSellBtn = ({ classes, isAuthenticated, href, label }) => {
  return (
    <Button
      className={
        isAuthenticated
          ? classes.sellButtonAuth
          : classes.sellButton
      }
      href={href}
      variant="extendedFab"
      onClick={() => analytics.track("sell_clicked", {})}
    >
      {label}
    </Button>
  );
};
const renderRegisterBtn = ({ classes, href, label }) => {
  return (
    <Button
      href={href}
      className={classes.registerButton}
      onClick={() => analytics.track("signup_clicked", {})}
    >
      {label}
    </Button>
  );
};
const renderLoginBtn = ({ classes, handleLogin, label }) => {
  return (
    <Button className={classes.loginButton} onClick={handleLogin} variant="extendedFab">
      {label}
    </Button>
  );
};

function phoneHref(num) {
  const digits = String(num || "").replace(/\D/g, "");
  return `sms:+1-${digits}`;
}

function getPhoneForHeader(lang) {
  if (lang === "fr") return PHONE_QC;
  if (lang === "en") return PHONE_DEFAULT;
  const guess = guessLangFromBrowser();
  if (guess === "fr") return PHONE_QC;
  return PHONE_DEFAULT;
}

const desktopNav = ({ classes, isAuthenticated, currUser, handleLogout, t, i18n, ...props }) => {
  const [isLoginFormOpen, setIsLoginFormOpen] = useState(false);

  const [lang, setLang] = useState(
    getLangFromUrl() || getSavedLang() || guessLangFromBrowser()
  );
  const phoneNumber = useMemo(() => getPhoneForHeader(lang), [lang]);

  const handleLogin = () => {
    analytics.track("login_opened", {});
    setIsLoginFormOpen(true);
  };
  const handleCloseLoginForm = () => setIsLoginFormOpen(false);

  useEffect(() => {
    const curr = (i18n && i18n.language) || "";
    const norm = curr.startsWith("fr") ? "fr" : curr.startsWith("en") ? "en" : curr;
    if (norm && norm !== lang) setLang(norm);
  }, [i18n && i18n.language]);
  const logoSrc = lang === "fr" ? LogoFr : Logo;

  // const persistPreferredLang = (l) => {
  //   try {
  //     if (!isAuthenticated || !currUser || !currUser.email) return;
  //     const email = String(currUser.email || "").toLowerCase();
  //     API.post("v2", "marketplace/users/update-preferred-lang", {
  //       body: {
  //         email,
  //         preferredLang: l,
  //       },
  //     }).catch(() => {});
  //   } catch (_) {}
  // };
  const persistPreferredLang = () => {};

  const chooseLang = (l) => {
    const curr = (i18n && i18n.language) || "";
    if (curr === l || curr.startsWith(l)) return;
    setLang(l);
    saveLang(l);
    if (getLangFromUrl() !== l) setLangInUrl(l);
    i18n.changeLanguage(l);
    persistPreferredLang(l);
    analytics.track("language_selected", { language: l });
  };

  const isMobile = typeof window !== "undefined" && window.screen && window.screen.width < 962;

  return (
    <div className={isMobile ? classes.navContainerMobile : classes.navContainer}>
      <nav className={classes.nav} position="relative">
        <a
          className={classes.callUsContainer}
          href={phoneHref(phoneNumber)}
          onClick={() => analytics.track("support_link_clicked", { channel: "sms", surface: "header", device_type: "desktop" })}
        >
          <span className={classes.questionsText}>{t("common.questions_text")}</span>
          <br />
          <span className={classes.phoneNumberText}>{phoneNumber}</span>
        </a>
        <div className={classes.logoContainer}>
          <a href="/" className={classes.logo}>
            <img src={logoSrc} alt="FansFirst Logo" className={classes.logo} />
          </a>
        </div>

        <div className={isAuthenticated ? classes.linksContainerAuth : classes.linksContainer}>
          <div className={classes.navLinks}>
            <div className={classes.langToggle} style={{ marginRight: isAuthenticated ? "16px" : 0 }} aria-label="Language switcher">
              <button
                className={`${classes.langBtn} ${lang === "fr" ? classes.langBtnActive : ""}`}
                onClick={() => chooseLang("fr")}
                type="button"
              >
                {t("common.lang_fr")}
              </button>
              <button
                className={`${classes.langBtn} ${lang === "en" ? classes.langBtnActive : ""}`}
                onClick={() => chooseLang("en")}
                type="button"
              >
                {t("common.lang_en")}
              </button>
            </div>
            {isAuthenticated && currUser ? "" : renderSellBtn({
              classes,
              isAuthenticated,
              href: withLang("/sell", lang),
              label: t("common.sell_tickets"),
            })}
            <div className={classes.accountLinksContainer} />
            {isAuthenticated && currUser ? (
              <AccountMenu currUser={currUser} handleLogout={handleLogout} />
            ) : (
              <React.Fragment>
                {renderRegisterBtn({
                  classes,
                  href: withLang("/signup", lang),
                  label: t("common.sign_up"),
                })}
                {renderLoginBtn({
                  classes,
                  handleLogin,
                  label: t("common.log_in"),
                })}
                {isLoginFormOpen && (
                  <LoginForm
                    isAuthenticated={isAuthenticated}
                    currUser={currUser}
                    handleLogout={handleLogout}
                    {...props}
                    isOpen={isLoginFormOpen}
                    handleClose={handleCloseLoginForm}
                  />
                )}
              </React.Fragment>
            )}
          </div>
        </div>
      </nav>
    </div>
  );
};

export default withStyles(styles)(withTranslation()(desktopNav));
